import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SchoolApi } from '../../../api/client';
import { Button } from '../../../components/ui';

export default function DesignatedCounselorCard() {
  const qc = useQueryClient();
  const settingsQ = useQuery({
    queryKey: ['school', 'institution-settings'],
    queryFn: () => SchoolApi.institutionSettings(),
  });
  const counselorsQ = useQuery({
    queryKey: ['school', 'counselors_in_institution'],
    queryFn: () => SchoolApi.counselorsInInstitution(),
  });

  const currentId = settingsQ.data?.designated_counselor?.profile_id ?? null;
  const [selected, setSelected] = useState<number | null>(currentId);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(currentId);
  }, [currentId]);

  const isDirty = selected !== currentId;

  const onSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await SchoolApi.setDesignatedCounselor(selected);
      qc.invalidateQueries({ queryKey: ['school', 'institution-settings'] });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: unknown } } | null)?.response?.data
          ? JSON.stringify((e as { response: { data: unknown } }).response.data)
          : 'Save failed';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-label="Wellbeing case assignment" className="flex min-w-0 flex-col gap-4 rounded-[22px] bg-navy p-6 text-on-navy">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">Wellbeing case assignment</h2>
          <p className="mt-2 font-display text-xl font-bold text-white">Who gets new wellbeing cases</p>
        </div>
        <span className="whitespace-nowrap rounded-full bg-navy-2 px-2 py-[3px] font-mono text-xs font-semibold text-on-navy">Counselor only</span>
      </div>
      <p className="text-[13px] text-on-navy-muted">
        Auto-opened wellbeing cases are assigned to this counselor by default.
      </p>

      <label className="sr-only" htmlFor="designated-counselor-sel">Designated counselor</label>
      <select
        id="designated-counselor-sel"
        aria-label="Designated counselor"
        value={selected == null ? '' : String(selected)}
        onChange={(e) => setSelected(e.target.value === '' ? null : Number(e.target.value))}
        className="min-h-[44px] w-full rounded-[12px] border border-navy-3 bg-surface px-3.5 text-sm text-ink outline-none focus:ring-2 focus:ring-accent"
      >
        <option value="">— First counselor (default)</option>
        {(counselorsQ.data?.teachers ?? []).map((c) => (
          <option key={c.profile_id} value={c.profile_id}>
            {c.first_name} {c.last_name} (Counselor)
          </option>
        ))}
      </select>

      {saveError && (
        <div role="alert" className="rounded-[12px] bg-navy-2 p-2.5 text-sm text-tag-text">
          {saveError}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={!isDirty || saving} loading={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </section>
  );
}
